<template>
  <!-- ==================== 고객사별 접수현황 ==================== -->
  <div :class="['customer-panel', { 'customer-panel--mobile': isMobile }]">
    <header class="customer-panel__header">
      <!-- 카드 옆 토글 — useDashboardFilter.periodStatus와 단일 source 동기화 -->
      <div class="customer-panel__period">
        <button
          v-for="period in periodOptions"
          :key="period.value"
          :class="[
            'customer-panel__period-pill',
            { 'customer-panel__period-pill--active': period.value === modelValue }
          ]"
          type="button"
          @click="$emit('update:modelValue', period.value)"
        >
          {{ t(period.labelKey) }}
        </button>
      </div>
    </header>

    <div v-if="items.length > 0" class="customer-panel__list">
      <article
        v-for="item in items"
        :key="item.partnerId"
        class="customer-card"
        tabindex="0"
        role="button"
        :aria-label="t('dashboard.customer.openDetailFor', { name: item.partnerName })"
        @click="goDetail(item)"
        @keydown.enter="goDetail(item)"
        @keydown.space.prevent="goDetail(item)"
      >
        <div class="customer-card__header">
          <span class="customer-card__name">{{ item.partnerName }}</span>
          <span class="customer-card__period">{{ periodLabel }}</span>
        </div>
        <div class="customer-card__metrics">
          <div class="customer-card__metric">
            <span class="customer-card__metric-label">{{ t('dashboard.customer.metrics.total') }}</span>
            <span class="customer-card__metric-value">{{ formatNumber(item.total) }}</span>
          </div>
          <div class="customer-card__metric customer-card__metric--pending">
            <span class="customer-card__metric-label">{{ t('dashboard.customer.metrics.pending') }}</span>
            <span class="customer-card__metric-value">{{ formatNumber(item.pending) }}</span>
          </div>
          <div class="customer-card__metric customer-card__metric--cancelled">
            <span class="customer-card__metric-label">{{ t('dashboard.customer.metrics.cancelled') }}</span>
            <span class="customer-card__metric-value">{{ formatNumber(item.cancelled) }}</span>
          </div>
          <div class="customer-card__metric customer-card__metric--completed">
            <span class="customer-card__metric-label">{{ t('dashboard.customer.metrics.completed') }}</span>
            <span class="customer-card__metric-value">{{ formatNumber(item.completed) }}</span>
          </div>
          <div class="customer-card__metric customer-card__metric--rate">
            <span class="customer-card__metric-label">{{ t('dashboard.customer.metrics.rate') }}</span>
            <span class="customer-card__metric-value">{{ formatRate(item.completionRate) }}</span>
          </div>
        </div>
      </article>
    </div>

    <div v-else-if="loading" class="customer-panel__state">
      {{ t('dashboard.customer.loading') }}
    </div>
    <div v-else class="customer-panel__state customer-panel__state--empty">
      {{ t('dashboard.customer.empty') }}
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import i18n from '@/locales/i18n'

const t = i18n.global.t
const router = useRouter()

const props = defineProps({
  /** 고객사별 5메트릭 배열 — items[i].{partnerId, partnerName, total, pending, cancelled, completed, completionRate} */
  items: { type: Array, default: () => [] },
  /** useDashboardFilter.periodStatus.value — TODAY/WEEK/MONTH/YEAR */
  modelValue: { type: String, default: 'MONTH' },
  loading: { type: Boolean, default: false },
  isMobile: { type: Boolean, default: false }
})

defineEmits(['update:modelValue'])

const periodOptions = [
  { value: 'YEAR', labelKey: 'button.thisYear' },
  { value: 'MONTH', labelKey: 'button.thisMonth' },
  { value: 'WEEK', labelKey: 'button.thisWeek' },
  { value: 'TODAY', labelKey: 'button.today' }
]

/**
 * 메인 토글의 periodStatus로부터 카드 상단의 라벨(예: "2026년 4월")을 derived 한다.
 * 별도 API 응답 필드 불필요 — 단일 source.
 */
const periodLabel = computed(() => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  switch (props.modelValue) {
    case 'YEAR':
      return t('dashboard.customer.label.year', { year })
    case 'MONTH':
      return t('dashboard.customer.label.month', { year, month })
    case 'WEEK':
      return t('dashboard.customer.label.week')
    case 'TODAY':
    default:
      return t('dashboard.customer.label.today')
  }
})

/**
 * 고객사 카드 클릭 시 RepairRequestHistory 페이지로 이동.
 * partnerId를 query로 전달하여 해당 고객사 필터링된 화면을 표시.
 *
 * @param {{partnerId: number, partnerName: string}} item
 */
function goDetail(item) {
  if (!item || !item.partnerId) return
  router.push({
    name: '접수 이력',
    query: { partnerId: item.partnerId }
  })
}

function formatNumber(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0'
  return num.toLocaleString()
}

function formatRate(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0%'
  return `${num.toFixed(1)}%`
}
</script>

<style scoped>
/* ========================================
   CustomerStatusListPanel
   ======================================== */
.customer-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 620px;
  overflow-y: auto;
  padding-right: 4px;
}

.customer-panel__header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 4px;
}

.customer-panel__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
}

.customer-panel__period {
  display: flex;
  gap: 4px;
}

.customer-panel__period-pill {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid var(--color-gray-200, #B8BCC4);
  background: var(--color-white, #fff);
  color: var(--color-gray-600, #424750);
  border-radius: var(--radius-full, 9999px);
  cursor: pointer;
  transition: all var(--transition-fast, 0.15s ease);
}

.customer-panel__period-pill:hover {
  border-color: var(--color-primary, #3B5998);
  color: var(--color-primary, #3B5998);
}

.customer-panel__period-pill--active {
  background: var(--color-primary, #3B5998);
  border-color: var(--color-primary, #3B5998);
  color: var(--color-white, #fff);
}

/* ===== Card ===== */
.customer-panel__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.customer-card {
  background: var(--color-gray-50, #F3F4F6);
  border: 1px solid transparent;
  border-radius: var(--radius-md, 8px);
  padding: 14px 16px;
  cursor: pointer;
  transition: border-color var(--transition-fast, 0.15s ease),
    background var(--transition-fast, 0.15s ease);
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
}

.customer-card:hover,
.customer-card:focus-visible {
  border-color: var(--color-primary, #3B5998);
  background: var(--color-white, #fff);
  outline: none;
}

.customer-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.customer-card__name {
  font-size: 14px;
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.customer-card__period {
  font-size: 11px;
  color: var(--color-gray-500, #5C616B);
  font-weight: 500;
  flex-shrink: 0;
}

.customer-card__metrics {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.customer-card__metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 4px;
  border-radius: var(--radius-sm, 4px);
  background: var(--color-white, #fff);
}

.customer-card__metric-label {
  font-size: 10px;
  color: var(--color-gray-500, #5C616B);
  font-weight: 500;
}

.customer-card__metric-value {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
  font-feature-settings: 'tnum';
}

.customer-card__metric--pending .customer-card__metric-value {
  color: var(--color-warning, #C77700);
}

.customer-card__metric--cancelled .customer-card__metric-value {
  color: var(--color-gray-500, #5C616B);
}

.customer-card__metric--completed .customer-card__metric-value {
  color: var(--color-success, #2E7D5B);
}

.customer-card__metric--rate .customer-card__metric-value {
  color: var(--color-primary, #3B5998);
}

/* ===== State (Loading / Empty) ===== */
.customer-panel__state {
  padding: 32px 16px;
  text-align: center;
  font-size: 13px;
  color: var(--color-gray-500, #5C616B);
}

.customer-panel__state--empty {
  font-style: italic;
}

/* ===== Mobile (<768px) ===== */
@media (max-width: 767px) {
  .customer-panel {
    max-height: 480px;
  }
  .customer-card {
    padding: 12px 14px;
  }
  .customer-card__metrics {
    grid-template-columns: repeat(5, 1fr);
    gap: 4px;
  }
  .customer-card__metric {
    padding: 4px 2px;
  }
  .customer-card__metric-value {
    font-size: 14px;
  }
  .customer-card__metric-label {
    font-size: 9px;
  }
}

/* ===== Tablet (768-1279px) ===== */
@media (min-width: 768px) and (max-width: 1279px) {
  .customer-card__metrics {
    grid-template-columns: repeat(5, 1fr);
  }
}
</style>
